import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import MDBox from 'components/MDBox';
import Card from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GroupElements/GroupItems/EditGroupItems/Card.js';
import { notify } from 'helpers/notificationHelper';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import useWindowDimensions from 'hooks/useWindowDimensions';
import useGroupItems from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GroupElements/GroupItems/hooks/useGroupItems.js';
import { updateGroup } from 'services/api/project';
import Grid from '@mui/material/Grid';
import { formatPrice, calculateDiscount } from 'helpers/priceHelpers';
import MDButton from 'components/MDButton';

const updateGroupItems = async (changedItemID, updateParams) => {
    try {
        const rp = await updateGroup(changedItemID, updateParams);
        notify(rp.data.success, rp.data.message);
        return rp;
    } catch (err) {
        notify(false, JSON.stringify(err));
        return false;
    }
};

const handleDragEnd = (result, columns, setColumns, demandID, queryClient) => {
    // const { result, columns, setColumns, demandID, queryClient } = params;
    if (!result.destination) return;

    const { source, destination } = result;
    const sourceColumn = columns.find((column) => column.groupID === source.droppableId);
    const destColumn = columns.find((column) => column.groupID === destination.droppableId);
    const sourceItems = [...sourceColumn.items];
    const destItems = destColumn ? [...destColumn.items] : [];
    const draggedItem = sourceItems[source.index];

    if (source.droppableId !== destination.droppableId) {
        // Remove item from source column
        sourceItems.splice(source.index, 1);
        // Insert item to destination column
        destItems.splice(destination.index, 0, draggedItem);

        if (isItemUnique(destItems, draggedItem)) {
            updateColumns({
                columns,
                setColumns,
                destColumn,
                items: destItems,
                draggedItem,
                demandID,
                queryClient,
                sourceItems,
            });
        } else {
            notify(false, 'Kalem grupta mevcut');
        }
    } else {
        // Reorder items in the same column
        sourceItems.splice(source.index, 1);
        sourceItems.splice(destination.index, 0, draggedItem);
        updateColumns({
            columns,
            setColumns,
            destColumn,
            items: sourceItems,
            draggedItem,
            demandID,
            queryClient,
        });
    }
};

const isItemUnique = (items, targetItem) => {
    const sameItems = items.filter((item) => item.ref._id === targetItem.ref._id);
    return sameItems.length < 2;
};

const convertToSchema = (arr) => {
    let schemaFormat = {
        uKasa: [],
        uPervaz: [],
        pervaz: [],
        kasa: [],
        kanat: [],
        diger: [],
    };
    let items = [];
    arr.forEach((item, index) => {
        if (schemaFormat.hasOwnProperty(item.type)) {
            schemaFormat[item.type].push({
                ref: item.ref,
                quantity: item.quantity,
                itemOrder: index,
                type: item.type,
            });
            items.push({
                ref: item.ref,
                quantity: item.quantity,
                itemOrder: index,
                type: item.type,
            });
        }
    });

    return { schemaFormat, items };
};
const updateColumns = async (params) => {
    const { columns, setColumns, destColumn, items, demandID, queryClient } = params;

    let { schemaFormat: schemeData, items: updatedItems } = convertToSchema(items);

    let updatedColumns = columns.map(
        (obj) =>
            obj.groupID === destColumn.groupID
                ? { ...obj, items: updatedItems } // Spread the object and replace the items
                : obj // If not the target group, leave it as it is
    );

    setColumns(updatedColumns);

    await updateGroupItems(destColumn.groupID, schemeData);

    queryClient.invalidateQueries({ queryKey: ['fetchDemandGroups', demandID] });
};

export default function Kanban({ demand, groups }) {
    const { demandItemsReduced, groupColumnsAndItems } = useGroupItems(demand, groups);

    const [columns, setColumns] = useState(
        [
            {
                _id: 'defaultID',
                groupID: 'defaultID',
                groupName: 'Kalemler',
                groupOrder: -1,
                items: demandItemsReduced,
            },
            ...groupColumnsAndItems,
        ].sort((a, b) => a.groupOrder - b.groupOrder)
    );

    // Her grup için ayrı divide değerleri tutacak state
    const [divideValues, setDivideValues] = useState({});

    const [defaultColumnZeroValuesAreVisible, setDefaultColumnZeroValuesAreVisible] = useState(true);

    const queryClient = useQueryClient();
    const demandID = demand._id;
    const { height, width } = useWindowDimensions();

    // Divide değerini güncelleme fonksiyonu
    const updateDivideValue = (groupID, value) => {
        setDivideValues((prev) => ({
            ...prev,
            [groupID]: value,
        }));
    };

    // Divide değerini alma fonksiyonu
    const getDivideValue = (groupID) => {
        return divideValues[groupID] || 1;
    };

    useEffect(() => {
        const groupsToRender = [
            {
                _id: 'defaultID',
                groupID: 'defaultID',
                groupName: 'Kalemler',
                groupOrder: -1,
                items: demandItemsReduced,
            },
            ...groupColumnsAndItems,
        ].sort((a, b) => a.groupOrder - b.groupOrder);

        setColumns(groupsToRender);
    }, [demandItemsReduced, groupColumnsAndItems]);

    // Default "Kalemler" grubunu ve diğer grupları ayır
    const defaultGroup = columns.find((group) => group.groupID === 'defaultID');
    const otherGroups = columns.filter((group) => group.groupID !== 'defaultID');

    return (
        <MDBox sx={{ width: '100%', overflow: 'hidden' }}>
            <DragDropContext onDragEnd={(result) => handleDragEnd(result, columns, setColumns, demandID, queryClient)}>
                {/* Default "Kalemler" grubu - üstte tek satır */}
                {defaultGroup && (
                    <MDBox sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <GroupContainer
                            group={defaultGroup}
                            width={width}
                            height={height}
                            key={defaultGroup._id}
                            demandItemsReduced={demandItemsReduced}
                            setDefaultColumnZeroValuesAreVisible={setDefaultColumnZeroValuesAreVisible}
                            defaultColumnZeroValuesAreVisible={defaultColumnZeroValuesAreVisible}
                            divide={getDivideValue(defaultGroup.groupID)}
                            setDivide={(value) => updateDivideValue(defaultGroup.groupID, value)}
                            isDefaultGroup={true}
                        />
                    </MDBox>
                )}

                {/* Diğer gruplar - altta kolon kolon */}
                {otherGroups.length > 0 && (
                    <MDBox sx={{ width: '100%' }}>
                        <Grid container spacing={2} sx={{ width: '100%', margin: 0, justifyContent: 'flex-start' }}>
                            {otherGroups.map((group, index) => (
                                <Grid
                                    key={group._id || index}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 6,
                                        lg: 6,
                                        xl: 4,
                                    }}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minWidth: 0,
                                    }}
                                >
                                    <GroupContainer
                                        group={group}
                                        width={width}
                                        height={height}
                                        key={group._id || index}
                                        demandItemsReduced={demandItemsReduced}
                                        setDefaultColumnZeroValuesAreVisible={setDefaultColumnZeroValuesAreVisible}
                                        defaultColumnZeroValuesAreVisible={defaultColumnZeroValuesAreVisible}
                                        divide={getDivideValue(group.groupID)}
                                        setDivide={(value) => updateDivideValue(group.groupID, value)}
                                        isDefaultGroup={false}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </MDBox>
                )}
            </DragDropContext>
        </MDBox>
    );
}

function GroupContainer({
    group,
    width,
    height,
    setDefaultColumnZeroValuesAreVisible,
    defaultColumnZeroValuesAreVisible,
    divide,
    setDivide,
    isDefaultGroup = false,
}) {
    const calculateTotalPrice = (items) => {
        return items.reduce((total, item) => {
            const discountedPrice = calculateDiscount(item.ref.verilenFiyat, item.quantity, item.ref.indirimOrani);
            return total + discountedPrice;
        }, 0);
    };

    const renderItems = (items) => {
        return items
            .sort((a, b) => a.itemOrder - b.itemOrder)
            .map((item, index) => (
                <Item
                    key={item?.ref._id}
                    item={item}
                    index={index}
                    groupID={group?.groupID}
                    columnItems={group.items}
                    demandID={group.demand}
                />
            ));
    };

    const totalPrice = calculateTotalPrice(group?.items || []);

    let visibleItems = group?.items;

    return (
        <MDBox
            color="dark"
            bgColor={'light'}
            variant="gradient"
            borderRadius="lg"
            shadow="lg"
            opacity={1}
            p={isDefaultGroup ? 0.75 : 1}
            m={isDefaultGroup ? 0 : 0}
            key={group?.groupID}
            sx={{
                width: isDefaultGroup ? 'fit-content' : '100%',
                height: 'fit-content',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                overflow: isDefaultGroup ? 'visible' : 'hidden',
            }}
        >
            {!isDefaultGroup && (
                <>
                    <MDTypography
                        variant="h6"
                        textTransform="capitalize"
                        fontWeight="medium"
                        textAlign="center"
                        sx={{ mb: 1 }}
                    >
                        {group?.groupName} - Set Fiyatı: {formatPrice(totalPrice / divide)}
                    </MDTypography>
                    <MDBox display="flex" justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                        <MDInput
                            type="number"
                            value={divide}
                            onChange={(e) => setDivide(e.target.value)}
                            placeholder="Örn: 1"
                        />
                    </MDBox>
                </>
            )}
            <Droppable droppableId={group?.groupID}>
                {(provided) => (
                    <MDBox
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{
                            padding: isDefaultGroup ? '8px 4px' : 1,
                            width: isDefaultGroup ? 'fit-content' : '100%',
                            minHeight: isDefaultGroup ? 'auto' : height / 6,
                            maxHeight: isDefaultGroup ? 'none' : height / 2,
                            overflow: isDefaultGroup ? 'visible' : 'auto',
                            display: isDefaultGroup ? 'flex' : 'flex',
                            flexDirection: isDefaultGroup ? 'row' : 'column',
                            flexWrap: isDefaultGroup ? 'wrap' : 'nowrap',
                            gap: isDefaultGroup ? 1.5 : 0,
                            justifyContent: isDefaultGroup ? 'flex-start' : 'flex-start',
                            alignItems: isDefaultGroup ? 'flex-start' : 'stretch',
                        }}
                    >
                        {renderItems(visibleItems)}
                        {provided.placeholder}
                    </MDBox>
                )}
            </Droppable>
        </MDBox>
    );
}

function Item({ item, groupID, columnItems, demandID, index }) {
    return (
        <Draggable draggableId={item.ref._id + groupID} index={index}>
            {(provided, snapshot) => (
                <MDBox
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{
                        padding: groupID === 'defaultID' ? 0.75 : 1,
                        margin: groupID === 'defaultID' ? '0' : '0 0 8px 0',
                        borderRadius: 2,
                        width: 'auto',
                        minWidth: '260px',
                        maxWidth: '320px',
                        flexShrink: groupID === 'defaultID' ? 0 : 1,
                        boxSizing: 'border-box',
                        ...provided.draggableProps.style,
                    }}
                    bgColor={colorDecider(groupID, item)}
                >
                    <Card item={item} groupID={groupID} demandID={demandID} columnItems={columnItems} />
                </MDBox>
            )}
        </Draggable>
    );
}

const colorDecider = (groupID, item) => {
    if (groupID == 'defaultID' && item.quantity < 0) {
        return 'primary';
    }
    if (item.quantity == 0) {
        return 'light';
    }
    return 'white';
};
